import { reactive, ref } from 'vue';
import { defineStore, storeToRefs } from 'pinia';

import useMemberStore from '@/store/storeMember';

const useCommonStore = defineStore('commonStore', () => {
  const { errorMessage, signupForm, editPasswordForm } = useMemberStore();
  const { confirmPassword } = storeToRefs(useMemberStore());

  const isMobile = ref<boolean>(false); // 모바일 여부
  const isLoading = ref<boolean>(false); // 로딩 여부

  // 단순 보이기
  const isMainLogo = ref<boolean>(false); // 메인 로고 보이기
  const isSatelliteInfo = ref<boolean>(true); // 위성 정보 보이기

  // 전환 효과
  const isImageMode = ref<boolean>(false); // 이미지 모드 활성화

  // 화면 사이즈 인지하기
  const windowSize = reactive({
    width: window.innerWidth, // 화면 가로 사이즈
    height: window.innerHeight, // 화면 세로 사이즈
  });

  // 패널 보이기
  const showPanel = reactive({
    isLogin: false, // 모바일 레이아웃 로그인 폼 보이기
    isLeftPanel: true, // PC 왼쪽 패널 보이기
    isSignUp: false, // 회원가입 폼 보이기
    isEditPassword: false, // 비밀번호 변경 폼 보이기
    isEditNickname: false, // 닉네임 변경 input 보이기
    isAccountInfo: false,  // 계정 정보 보이기
    isMobileAccountInfo: false, // 모바일 버전 계정 정보 보이기
  });

  // 모달창 보이기
  const showModal = reactive({
    isForgotAccount: false, // 'Forgot Account' 모달창 보이기
    isLicense: false, // 저작권 모달창 보이기
    isSuccess: false, // 성공 모달창 보이기
    isFail: false, // 실패 모달창 보이기
  });

  // 모달창 내용
  const modalContents = reactive({
    title: '', // 제목
    description: '', // 내용
  });

  /**
   * 모바일 레이아웃 로그인 패널 보이기 토글
   */
  const showLoginToggle = (): void => {
    showPanel.isLogin = !showPanel.isLogin;
  };

  /**
   * 회원가입 패널 보이기 토글
   */
  const showSignupToggle = (): void => {
    showPanel.isSignUp = !showPanel.isSignUp;

    if (showPanel.isSignUp) {
      confirmPassword.value = '';
      Object.assign(signupForm, {
        name: '',
        email: '',
        nickname: '',
        password: '',
      });
      Object.assign(errorMessage, {
        email: '',
        confirmPassword: '',
      });
    }
  };

  /**
   * 저작권 모달창 보이기 토글
   */
  const LicenseModalToggle = (): void => {
    showModal.isLicense = !showModal.isLicense;
  };

  /**
   * PC 왼쪽 패널 숨기기
   */
  const hideLeftPanel = (): void => {
    showPanel.isLeftPanel = false;
    isMainLogo.value = true;
  };

  /**
   * PC 왼쪽 패널 보이기
   */
  const showLeftPanel = (): void => {
    showPanel.isLeftPanel = true;
    if (isImageMode.value || showPanel.isLeftPanel) {
      isMainLogo.value = false;
    }
  };

  /**
   * 계정 정보 보이기 토글
   */
  const showAccountToggle = (): void => {
    showPanel.isAccountInfo = !showPanel.isAccountInfo;

    if (!showPanel.isLeftPanel) {
      showPanel.isLeftPanel = true;
    }
    if (showPanel.isEditPassword) {
      showPanel.isEditPassword = false;
    }
  };

  /**
   * 이미지 모드 활성화 토글
   */
  const imageModeToggle = (): void => {
    if (isImageMode.value) {
      isImageMode.value = false;
      isSatelliteInfo.value = true;
    } else {
      isImageMode.value = true;
      isSatelliteInfo.value = false;
    }
  };

  /**
   * input value의 유무에 따라 'active'클래스를 부여하여 css 애니메이션이 실행됩니다.
   */
  const inputValueAni = ((event: Event): void => {
    const target = <HTMLInputElement>event.target;

    if (target.value) {
      target.parentElement?.classList.add('active');
    } else {
      target.parentElement?.classList.remove('active');
      if (!signupForm.email) {
        errorMessage.email = '';
      }
      if (!editPasswordForm.current) {
        errorMessage.currentPassword = '';
      }
    }
  });

  /**
   * 모달창이 활성화 되면 input과 button이 비활성화 됩니다.
   */
  const disabledQualification = (): boolean => {
    return showModal.isForgotAccount || showModal.isSuccess || showModal.isFail;
  };

  /**
   * 모달창을 닫습니다.
   */
  const closeModal = (): void => {
    isMainLogo.value = false;
    isSatelliteInfo.value = true;

    if (showModal.isSuccess) {
      showModal.isSuccess = false;
      showPanel.isSignUp = false;
      showPanel.isEditPassword = false;
    } else if (showModal.isFail) {
      showModal.isFail = false;
    } else if (showModal.isForgotAccount) {
      showModal.isForgotAccount = false;
    } else if (showModal.isLicense) {
      showModal.isLicense = false;
    }
  };

  return {
    isMobile,
    isLoading,
    isMainLogo,
    isSatelliteInfo,
    isImageMode,
    windowSize,
    showPanel,
    showModal,
    modalContents,
    showLoginToggle,
    showSignupToggle,
    LicenseModalToggle,
    hideLeftPanel,
    showLeftPanel,
    showAccountToggle,
    imageModeToggle,
    inputValueAni,
    disabledQualification,
    closeModal,
  };
});

export default useCommonStore;
